import React, { useEffect, useRef } from "react";
import { ClassNames } from "@emotion/react";
import styled from "@emotion/styled";
import { Core } from "@pdftron/webviewer";
import { Skeleton } from "antd5";
import { Flex } from "styles/utility-components";

import { SimpleMatchHighlighter } from "lib/core_components/TextMatchHighlighter";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { pluralise } from "lib/utils";
import { useKeyboardShortcut } from "../../lib/hooks/useKeyboardShortcut";
import EmptyDocument from "./EmptyDocument";

export type SearchResultSnippet = Core.Search.SearchResult & { originalIndex: number };

type DocumentSearchResultsProps = {
  activeResultIndex: number;
  setActiveResultIndex: (index: number) => void;
  searchResults: SearchResultSnippet[] | null;
  onJumpToSearchResult: (result: SearchResultSnippet) => void;
  isLoading?: boolean;
  filterCount: number;
  isError?: boolean;
};

export default function DocumentSearchResults({
  activeResultIndex,
  setActiveResultIndex,
  searchResults,
  onJumpToSearchResult,
  isLoading,
  filterCount,
  isError,
}: DocumentSearchResultsProps) {
  const handleSetActiveSearchResult = (index: number, result: SearchResultSnippet) => {
    setActiveResultIndex(index);
    onJumpToSearchResult(result);
  };

  const jumpToPreviousMatch = () => {
    const index = activeResultIndex - 1;

    if (searchResults && index >= 0) {
      const searchResult = searchResults[index];
      handleSetActiveSearchResult(index, searchResult);
    }
  };

  const jumpToNextMatch = () => {
    const index = activeResultIndex + 1;

    if (searchResults && index < searchResults.length) {
      const searchResult = searchResults[index];
      handleSetActiveSearchResult(index, searchResult);
    }
  };

  useKeyboardShortcut(["ArrowUp"], jumpToPreviousMatch);
  useKeyboardShortcut(["ArrowDown"], jumpToNextMatch);

  return (
    <Results>
      <Header>
        <Count>{pluralise(searchResults?.length ?? 0, "Result")}</Count>
        <Flex gap={4}>
          <ArrowContainer
            data-disabled={!searchResults?.length || activeResultIndex === 0}
            aria-label="Previous search result"
            onClick={jumpToPreviousMatch}
          >
            <UIcon icon="chevronUp" size={16} />
          </ArrowContainer>
          <ArrowContainer
            data-disabled={!searchResults?.length || activeResultIndex === searchResults.length - 1}
            aria-label="Next search result"
            onClick={jumpToNextMatch}
          >
            <UIcon icon="chevronDown" size={16} />
          </ArrowContainer>
        </Flex>
      </Header>
      <ResultsContainer>
        {filterCount > 0 && (
          <SearchResultsContainer
            isLoading={isLoading}
            searchResults={searchResults}
            handleSetActiveSearchResult={handleSetActiveSearchResult}
            activeResultIndex={activeResultIndex}
            isError={isError}
          />
        )}
      </ResultsContainer>
    </Results>
  );
}

function SearchResultsContainer({
  isLoading,
  searchResults,
  handleSetActiveSearchResult,
  activeResultIndex,
  isError,
}: {
  isLoading?: boolean;
  searchResults: SearchResultSnippet[] | null;
  handleSetActiveSearchResult: (index: number, result: SearchResultSnippet) => void;
  activeResultIndex: number;
  isError?: boolean;
}) {
  const searchResultsByPage = searchResults?.reduce<Record<number, SearchResultSnippet[]>>(
    (acc, result) => {
      const pageNum = result.pageNum;
      if (!acc[pageNum]) {
        acc[pageNum] = [];
      }
      acc[pageNum].push(result);
      return acc;
    },
    {},
  );

  const resultRefs = useRef<(HTMLDivElement | null)[]>([]);

  // scroll to the active result
  useEffect(() => {
    // defer scrolling to the next frame to ensure DOM is rendered
    requestAnimationFrame(() => {
      if (resultRefs.current[activeResultIndex]) {
        resultRefs.current[activeResultIndex]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    });
  }, [activeResultIndex]);

  if (isError) {
    return <div>Sorry, something has gone wrong.</div>;
  }

  if (isLoading) {
    return <Skeleton active />;
  }

  if (searchResults && searchResults.length === 0) {
    return <EmptyDocument />;
  }

  return (
    <Flex column gap={24}>
      {Object.entries(searchResultsByPage ?? []).map(([pageNum, results], index) => (
        <div key={index}>
          <Label>Page {pageNum}</Label>
          <Flex column gap={12}>
            {results.map((result) => (
              <Snippet
                key={`${result.originalIndex}`}
                ref={(el) => (resultRefs.current[result.originalIndex] = el)}
                onClick={() => handleSetActiveSearchResult(result.originalIndex, result)}
                data-active={activeResultIndex === result.originalIndex}
                data-testid="result-snippet"
              >
                <ClassNames>
                  {({ css, theme }) => (
                    <SimpleMatchHighlighter
                      text={result.ambientStr}
                      matches={[{ start: result.resultStrStart, end: result.resultStrEnd }]}
                      highlightClassName={css({
                        backgroundColor: theme.colors.sysPrimarySubtleAlt,
                      })}
                    />
                  )}
                </ClassNames>
              </Snippet>
            ))}
          </Flex>
        </div>
      ))}
    </Flex>
  );
}

const Results = styled.div({
  width: 440,
  display: "flex",
  flexDirection: "column",
  flex: "1 1 0",
});

const Header = styled.div({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: 24,
  "& h3": { marginBottom: 0 },
});

const Count = styled.h3({
  fontSize: 12,
  fontWeight: 400,
});

const ArrowContainer = styled.div(({ theme }) => ({
  cursor: "pointer",
  padding: 8,
  userSelect: "none",
  '&[data-disabled="true"]': {
    cursor: "default !important",
    color: theme.colors.sysTextInactive,
    "& svg": { cursor: "default !important" },
  },
}));

const Label = styled.h3(({ theme }) => ({
  fontSize: 12,
  color: theme.colors.sysTextInactive,
  marginBottom: 4,
}));

const Snippet = styled.div(({ theme }) => ({
  wordWrap: "break-word",
  cursor: "pointer",
  '&[data-active="true"]': {
    backgroundColor: theme.colors.sysPrimarySubtle,
    border: `1px solid ${theme.colors.sysPrimaryDefault}`,
    borderRadius: 4,
    padding: 8,
  },
}));

const ResultsContainer = styled.div({
  display: "flex",
  flexDirection: "column",
  padding: 24,
});
